import { useCallback, useState } from "react";

export interface PendingCapitalAdjustment {
  trait: string;
  deathCapital: number;
  adjustedCapitals: Record<string, number>;
}

interface UsePendingCapitalAdjustmentReturn {
  pendingAdjustment: PendingCapitalAdjustment | null;
  captureAdjustmentError: (
    error: unknown,
    currentCapitals: Record<string, number>
  ) => boolean;
  confirmAdjustment: (
    onAccept: (adjustedCapitals: Record<string, number>) => Promise<void> | void
  ) => Promise<void>;
  cancelAdjustment: () => void;
}

const ADJUSTMENT_REGEX =
  /(\w+) capital \(\d+\) cannot exceed DEATH capital \((\d+)\)/;

const matchAdjustment = (error: unknown): RegExpMatchArray | null => {
  if (!error || typeof error !== "object") return null;
  const maybe = error as { error?: unknown; message?: unknown };
  const candidates = [maybe.message, maybe.error].filter(
    (value): value is string => typeof value === "string"
  );
  for (const candidate of candidates) {
    const match = candidate.match(ADJUSTMENT_REGEX);
    if (match) return match;
  }
  return null;
};

export const usePendingCapitalAdjustment = (): UsePendingCapitalAdjustmentReturn => {
  const [pendingAdjustment, setPendingAdjustment] =
    useState<PendingCapitalAdjustment | null>(null);

  const captureAdjustmentError = useCallback(
    (error: unknown, currentCapitals: Record<string, number>): boolean => {
      const match = matchAdjustment(error);
      if (!match) return false;

      const trait = match[1];
      const deathCapital = parseInt(match[2], 10);
      if (!(trait in currentCapitals) || Number.isNaN(deathCapital)) {
        return false;
      }

      setPendingAdjustment({
        trait,
        deathCapital,
        adjustedCapitals: { ...currentCapitals, [trait]: deathCapital },
      });
      return true;
    },
    []
  );

  const confirmAdjustment = useCallback(
    async (
      onAccept: (
        adjustedCapitals: Record<string, number>
      ) => Promise<void> | void
    ) => {
      if (!pendingAdjustment) return;
      const { adjustedCapitals } = pendingAdjustment;
      setPendingAdjustment(null);
      await onAccept(adjustedCapitals);
    },
    [pendingAdjustment]
  );

  const cancelAdjustment = useCallback(() => {
    setPendingAdjustment(null);
  }, []);

  return {
    pendingAdjustment,
    captureAdjustmentError,
    confirmAdjustment,
    cancelAdjustment,
  };
};

export default usePendingCapitalAdjustment;
